import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Chrome, Globe, Download, Copy, Check, Puzzle, Bookmark, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const CHROME_WEB_STORE_URL = 'https://chromewebstore.google.com/detail/realprop-magic-scrape/lleakomiiomdmnlcbhifidebfdjkphne';

function detectBrowser() {
  const ua = navigator.userAgent;
  if (ua.includes('Edg/')) return 'edge';
  if (ua.includes('Chrome') && !ua.includes('Edg/')) return 'chrome';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'safari';
  if (ua.includes('Firefox')) return 'firefox';
  return 'other';
}

const BROWSER_INFO = {
  chrome: { name: 'Google Chrome', icon: Chrome, method: 'extension', color: '#4285F4' },
  edge: { name: 'Microsoft Edge', icon: Chrome, method: 'extension', color: '#0078D4' },
  safari: { name: 'Safari', icon: Globe, method: 'bookmarklet', color: '#007AFF' },
  firefox: { name: 'Firefox', icon: Globe, method: 'bookmarklet', color: '#FF7139' },
  other: { name: 'Your Browser', icon: Globe, method: 'bookmarklet', color: '#6B7280' },
};

export default function ClipperSetupPage() {
  const navigate = useNavigate();
  const [browser] = useState(detectBrowser);
  const [bookmarklet, setBookmarklet] = useState('');
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState(BROWSER_INFO[browser].method);
  const [showAdvancedInstall, setShowAdvancedInstall] = useState(false);
  const bookmarkletRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/clipper/bookmarklet`)
      .then(r => r.json())
      .then(d => setBookmarklet(d.bookmarklet || ''))
      .catch(() => {});
  }, []);

  // Set bookmarklet href via DOM ref (React blocks javascript: URLs)
  useEffect(() => {
    if (bookmarkletRef.current && bookmarklet) {
      bookmarkletRef.current.setAttribute('href', bookmarklet);
    }
  }, [bookmarklet, activeTab]);

  const handleCopy = () => {
    navigator.clipboard.writeText(bookmarklet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const info = BROWSER_INFO[browser];

  return (
    <div className="min-h-screen bg-background">
      <div className="noise-texture" />

      {/* Header */}
      <header className="sticky top-0 z-50 glassmorphic">
        <div className="container mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground" data-testid="clipper-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="clipper-setup-title">Browser Clipper Setup</h1>
            <p className="text-xs text-muted-foreground">Scrape any listing in one click</p>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-2xl">
        {/* Browser Detection */}
        <div className="rounded-xl border border-border/50 bg-card p-5 mb-6" data-testid="browser-detect-card">
          <div className="flex items-center gap-3 mb-1">
            <info.icon className="h-5 w-5" style={{ color: info.color }} />
            <span className="text-sm font-medium" data-testid="detected-browser">Detected: {info.name}</span>
          </div>
          <p className="text-xs text-muted-foreground ml-8">
            {info.method === 'extension'
              ? 'Chrome Extension recommended for the best experience.'
              : 'Bookmarklet works great in your browser — no install needed.'}
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-2 mb-6" data-testid="method-tabs">
          <button
            onClick={() => setActiveTab('extension')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'extension' ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-muted-foreground hover:text-foreground'}`}
            data-testid="tab-extension"
          >
            <Puzzle className="h-4 w-4" /> Chrome Extension
          </button>
          <button
            onClick={() => setActiveTab('bookmarklet')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'bookmarklet' ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-muted-foreground hover:text-foreground'}`}
            data-testid="tab-bookmarklet"
          >
            <Bookmark className="h-4 w-4" /> Bookmarklet
          </button>
        </div>

        {/* Extension Instructions */}
        {activeTab === 'extension' && (
          <div className="space-y-4" data-testid="extension-instructions">
            <div className="rounded-xl border border-border/50 bg-card p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-semibold">Install from Chrome Web Store</h2>
                <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                  Verified
                </span>
              </div>
              <p className="text-sm text-muted-foreground mb-5">
                One-click install on Chrome and Edge. Adds a floating <strong className="text-purple-400">R</strong> orb on real estate sites + a right-click context menu.
              </p>
              <ol className="space-y-4 text-sm">
                <Step n={1}>
                  Click the <strong>Add to Chrome</strong> button below — it opens the Chrome Web Store listing.
                </Step>
                <Step n={2}>
                  On the Web Store page, click <strong>Add to Chrome</strong> → <strong>Add extension</strong>.
                </Step>
                <Step n={3}>
                  Visit any listing on Zillow, Realtor.com, Redfin, etc. and click the floating <strong className="text-purple-400">R</strong> orb. The first scrape will prompt you to sign in to RealProp.
                </Step>
              </ol>

              <div className="mt-6 flex flex-wrap gap-3">
                <Button
                  variant="default"
                  className="gap-2"
                  data-testid="install-extension-webstore-btn"
                  onClick={() => window.open(CHROME_WEB_STORE_URL, '_blank', 'noopener,noreferrer')}
                >
                  <Chrome className="h-4 w-4" /> Add to Chrome
                  <ExternalLink className="h-3.5 w-3.5 opacity-70" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-muted-foreground"
                  onClick={() => setShowAdvancedInstall((v) => !v)}
                  data-testid="toggle-advanced-install-btn"
                >
                  {showAdvancedInstall ? 'Hide' : 'Show'} advanced install (developer)
                </Button>
              </div>

              {showAdvancedInstall && (
                <div className="mt-5 pt-5 border-t border-border/50" data-testid="advanced-install-instructions">
                  <p className="text-xs text-muted-foreground mb-4">
                    Only needed if the Web Store is blocked by your organization or you want to test a pre-release build.
                  </p>
                  <ol className="space-y-3 text-sm">
                    <Step n={1}>Download the extension ZIP below and unzip it.</Step>
                    <Step n={2}>Open <code className="px-1.5 py-0.5 bg-muted rounded text-xs">chrome://extensions</code>.</Step>
                    <Step n={3}>Enable <strong>Developer mode</strong> (top-right toggle).</Step>
                    <Step n={4}>Click <strong>Load unpacked</strong> and select the unzipped folder.</Step>
                  </ol>
                  <div className="mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      data-testid="download-extension-btn"
                      onClick={() => window.open(`${API}/api/extension/download`, '_blank')}
                    >
                      <Download className="h-4 w-4" /> Download ZIP
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <SupportedSites />
          </div>
        )}

        {/* Bookmarklet Instructions */}
        {activeTab === 'bookmarklet' && (
          <div className="space-y-4" data-testid="bookmarklet-instructions">
            <div className="rounded-xl border border-border/50 bg-card p-6">
              <h2 className="text-base font-semibold mb-4">Install Bookmarklet</h2>
              <p className="text-sm text-muted-foreground mb-5">Recommended for <strong>Safari</strong> and <strong>Firefox</strong> (no extension required). Drag the button below to your bookmarks bar, then click it on any listing page.</p>

              <ol className="space-y-4 text-sm">
                <Step n={1}>
                  Show your bookmarks bar (<kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">Cmd+Shift+B</kbd> on Mac, <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">Ctrl+Shift+B</kbd> on Windows).
                </Step>
                <Step n={2}>
                  Drag this button to your bookmarks bar:
                  <div className="mt-3">
                    {bookmarklet ? (
                      <a
                        ref={bookmarkletRef}
                        href="#"
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold no-underline cursor-grab active:cursor-grabbing shadow-lg shadow-purple-500/20"
                        onClick={(e) => { e.preventDefault(); }}
                        draggable="true"
                        data-testid="bookmarklet-drag-btn"
                      >
                        <span className="text-base font-extrabold">R</span> RealProp Clipper
                      </a>
                    ) : (
                      <span className="text-muted-foreground text-xs">Loading bookmarklet...</span>
                    )}
                  </div>
                </Step>
                <Step n={3}>
                  Navigate to any property listing and click <strong>RealProp Clipper</strong> in your bookmarks bar.
                </Step>
              </ol>

              {bookmarklet && (
                <div className="mt-5 pt-4 border-t border-border/50">
                  <p className="text-xs text-muted-foreground mb-2">Or copy the bookmarklet code manually:</p>
                  <div className="flex gap-2">
                    <code className="flex-1 px-3 py-2 bg-muted rounded-lg text-xs truncate" data-testid="bookmarklet-code">{bookmarklet}</code>
                    <Button variant="outline" size="sm" onClick={handleCopy} className="gap-1.5 shrink-0" data-testid="copy-bookmarklet-btn">
                      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      {copied ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <SupportedSites />
          </div>
        )}
      </main>
    </div>
  );
}

function Step({ n, children }) {
  return (
    <li className="flex gap-3">
      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">{n}</span>
      <div className="pt-0.5 text-muted-foreground leading-relaxed">{children}</div>
    </li>
  );
}

function SupportedSites() {
  const sites = [
    'Zillow', 'Realtor.com', 'Redfin', 'Trulia', 'Realtor.ca',
    'RE/MAX', 'Century 21', 'Compass', 'Coldwell Banker',
    'Sotheby\'s', 'KW', 'Royal LePage', 'REW.ca', 'Zolo.ca',
    'HouseSigma', 'Point2Homes', 'Condos.ca', 'Homes.com',
    'Movoto', 'HAR.com', 'John R. Wood', 'Homesnap', 'Estately',
  ];

  return (
    <div className="rounded-xl border border-border/50 bg-card p-5" data-testid="supported-sites-card">
      <h3 className="text-sm font-semibold mb-3">Supported Sites</h3>
      <div className="flex flex-wrap gap-1.5">
        {sites.map(s => (
          <span key={s} className="px-2.5 py-1 bg-muted rounded-md text-xs text-muted-foreground">{s}</span>
        ))}
        <span className="px-2.5 py-1 bg-primary/10 text-primary rounded-md text-xs font-medium">+ any MLS site</span>
      </div>
    </div>
  );
}
